import React, { useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Container, Typography, Box, Paper, Tabs, Tab, Fab, Tooltip, CircularProgress, Alert } from '@mui/material';
import { Chat } from '@mui/icons-material';
import FileUpload from './components/FileUpload';
import AnalysisDisplay from './components/AnalysisDisplay';
import ChatModal from './components/ChatModal';
import './App.css';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 500,
    },
  },
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function App() {
  const [tabValue, setTabValue] = useState(0);
  const [analysisData, setAnalysisData] = useState<any>(null);
  const [reportId, setReportId] = useState<string | null>(null);
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAnalysisStart = () => {
    setIsAnalyzing(true);
  };

  const handleAnalysisComplete = (data: any, id: string) => {
    setAnalysisData(data);
    setReportId(id);
    setIsAnalyzing(false);
    setTabValue(1); // Switch to analysis tab
  };

  const handleOpenChat = () => {
    setChatModalOpen(true);
  };

  const handleCloseChat = () => {
    setChatModalOpen(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom color="primary">
            🧬 Biomarker Analysis Platform
          </Typography>
          <Typography variant="h6" color="text.secondary">
            AI-Powered Blood Test Analysis with Evidence-Based Insights
          </Typography>
        </Box>

        <Paper elevation={3} sx={{ borderRadius: 2 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} centered>
              <Tab label="📄 Upload Report" />
              <Tab label="📊 Analysis Results" disabled={!analysisData} />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <FileUpload 
              onAnalysisComplete={handleAnalysisComplete} 
              onAnalysisStart={handleAnalysisStart}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {isAnalyzing ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                minHeight: 400,
                gap: 2 
              }}>
                <CircularProgress size={60} />
                <Typography variant="h6" color="text.secondary">
                  Analyzing your biomarker data...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This may take a few moments while we process your results
                </Typography>
              </Box>
            ) : (
              <AnalysisDisplay analysisData={analysisData} reportId={reportId} />
            )}
          </TabPanel>
        </Paper>

        {/* Floating Chat Button */}
        {analysisData && (
          <Tooltip title="Open AI Chat" placement="left">
            <Fab
              color="primary"
              aria-label="chat"
              onClick={handleOpenChat}
              sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                zIndex: 1000,
              }}
            >
              <Chat />
            </Fab>
          </Tooltip>
        )}

        {/* Chat Modal */}
        <ChatModal
          open={chatModalOpen}
          onClose={handleCloseChat}
          analysisData={analysisData}
          reportId={reportId}
        />
      </Container>
    </ThemeProvider>
  );
}

export default App;